import React, {useMemo, useState} from "react";
import classnames from "classnames";
import {Skeleton} from "@mui/material";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import {useTranslation} from "react-i18next";
import useCountryProvinceCache from "../../../../../../../hooks/use-country-province-cache";
import VendorAddressCardActionsPopover, {VendorAddressCardActions} from "./actions-popover";

export {VendorAddressCardActions};

/**
 * Vendor address card styled like customer addresses-v2 (line1–4; province/country from codes).
 */
const VendorAddressCard = ({
    address,
    remove,
    edit,
    setDefault,
    disabled: disabledProps = false,
    readonly = false,
    excludeActions = [],
    allowRemoval = true,
    allowSetDefault = true,
    settingAsDefault = false,
}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.vendor-account-info.addresses'});
    const [removing, setRemoving] = useState(false);
    const isMounted = useIsMounted();
    const {countries} = useCountryProvinceCache(undefined, true);

    const disabled = useMemo(() => removing || disabledProps, [removing, disabledProps]);

    const actions = useMemo(() => {
        const all = Object.values(VendorAddressCardActions);
        const excluded = Array.from(excludeActions ?? []);
        if (address?.isDefault || !allowSetDefault) {
            excluded.push(VendorAddressCardActions.setDefault);
        }
        if (!allowRemoval) {
            excluded.push(VendorAddressCardActions.remove);
        }
        return all.filter((a) => !excluded.includes(a));
    }, [address?.isDefault, allowRemoval, allowSetDefault, excludeActions]);

    const addressText = useMemo(() => {
        if (!address)
            return '';
        let res = '';
        const lines = [address.line1, address.line2, address.line3, address.line4].filter(Boolean);
        lines.forEach((line) => {
            res = res.concat(line, '<br/>');
        });
        if (address.city?.length) {
            res = res.concat(address.city, ', ');
        }
        const countryCode = address.countryCode
            ?? (typeof address.country === 'string' ? address.country : address.country?.code)
            ?? '';
        const provinceCode = address.provinceCode
            ?? (typeof address.province === 'string' ? address.province : address.province?.code)
            ?? '';
        const country = countries.find((c) => c.code === countryCode);
        const province = country?.provinces?.find((p) => p.code === provinceCode);
        const provinceName = province?.name ?? province?.title ?? provinceCode;
        if (provinceName) {
            res = res.concat(provinceName, ', ');
        }
        if (address.postalCode?.length) {
            res = res.concat(String(address.postalCode).toUpperCase(), ', ');
        }
        const countryName = country?.name ?? country?.title ?? countryCode;
        if (countryName) {
            res = res.concat(countryName);
        }
        return res;
    }, [address, countries]);

    /**
     * Removes the address through the parent callback while showing local busy state.
     */
    const removeAddress = async () => {
        setRemoving(true);
        await remove?.();
        if (!isMounted())
            return;
        setRemoving(false);
    };

    return (
        <div className={classnames('address-card')}>
            <div className={'address-card-header'}>
                <div className={'address-title-section'}>
                    <p className={'title'}>
                        {address?.title || address?.line1 || t('untitled')}
                    </p>
                    <p className={'type'}>
                        {address?.typeLabel ?? ''}
                    </p>
                </div>
                <div className={'d-flex align-items-center justify-content-end'}>
                    {
                        !readonly && !!actions.length &&
                        <VendorAddressCardActionsPopover
                            actions={actions}
                            disabled={disabled}
                            edit={edit}
                            setDefault={setDefault}
                            settingAsDefault={settingAsDefault}
                            {...(remove ? {remove: removeAddress} : {})}
                        />
                    }
                </div>
            </div>
            <div className={'address-card-content'}>
                <p dangerouslySetInnerHTML={{__html: addressText}}/>
            </div>
            <div className={'address-card-footer'}>
                <div>
                    {
                        settingAsDefault
                            ? (
                                <Skeleton sx={{transform: 'none'}}>
                                    <div className={'default-indicator'}>
                                        {t('defaultShipping')}
                                    </div>
                                </Skeleton>
                            )
                            : address?.isDefault
                                ? (
                                    <div className={'default-indicator'}>
                                        {t('defaultShipping')}
                                    </div>
                                )
                                : null
                    }
                </div>
            </div>
        </div>
    );
};

export default VendorAddressCard;
